/* eslint-disable no-nested-ternary */
import { CaretRightOutlined } from "@ant-design/icons";
import { SchoolCourse } from "@axl-dev/school-sdk";
import { Button, Tooltip } from "antd";
import { useCallback } from "react";

import { useLoc, useNotifier, useRouter, useSdk } from "@app/shared/lib/hooks";
import { DateTime, Loc } from "@app/shared/ui";

type Props = {
  course: SchoolCourse;
  buttonRef: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>;
  buttonLabels?: { continue?: string; start?: string };
};

export const NavigateButton: React.FC<Props> = ({ course, buttonRef, buttonLabels }) => {
  const loc = useLoc();
  const sdk = useSdk();
  const { history } = useRouter();
  const [notify] = useNotifier();

  const courseRequirementsBtn = useCallback(
    (requirements: string[]) => {
      const needToCompleteLabel = course.isCompleteAnyCourseRequirement ? (
        <Loc
          word="StudentCourse.needToCompleteAnyLabel"
          default="Need to complete any of"
        >
          {requirements.map((x) => `"${x}"`).join(",")}
        </Loc>
      ) : (
        <Loc
          word="StudentCourse.needToCompleteLabel"
          default="Need to complete"
        >
          {requirements.map((x) => `"${x}"`).join(",")}
          <Loc
            number={requirements.length}
            wordPlural={{
              one: "Course.plural.one",
              two: "Course.plural.two",
              other: "Course.plural.other",
            }}
            case="lower"
          />
        </Loc>
      );

      return (
        <Tooltip title={needToCompleteLabel}>
          <Button
            className="material-card-main-btn"
            disabled
            type="primary"
            data-testid="course-button"
          >
            <Loc word="Start" default="Start" />
            &nbsp;
            <CaretRightOutlined />
          </Button>
        </Tooltip>
      );
    },
    [course.isCompleteAnyCourseRequirement],
  );

  const courseWillBeAvailableBtn = useCallback(
    (accessDate?: Date) => {
      return accessDate ? (
        <Button
          className="material-card-main-btn material-card-main-btn-wrap"
          disabled
          type="primary"
          data-testid="course-button"
        >
          <span>{loc.word("School.contentItem.willBeAvailable", { default: "Will be available" })}</span>
          <DateTime date={accessDate} format="lll" />
        </Button>
      ) : (
        <Button
          className="material-card-main-btn "
          disabled
          type="primary"
          data-testid="course-button"
        >
          {`${loc.word("StudentCourse.noAccessLabel", { default: "You do not have access to this course" })}`}
        </Button>
      );
    },
    [loc],
  );

  const startOrContinue = useCallback(
    async (startOrContCourse: SchoolCourse) => {
      let currentCourseItemId: string;
      if (startOrContCourse.studentCourse?.currentCourseItem)
        currentCourseItemId =
          startOrContCourse.studentCourse.currentCourseItem.id;
      else {
        const result = await sdk.api.education.studentCourse.startCourse(
          startOrContCourse.id,
        );
        if (!result.success) {
          notify(result);

          return;
        }

        currentCourseItemId = result.body.currentCourseItemId;
      }

      history.push(`/learn/${currentCourseItemId}`);
    },
    [history, notify, sdk.api.education.studentCourse],
  );

  return (
    <div className="material-card-btns">
      {course.hasCourseItem ? (
        course.studentHasAccess ? (
          <Button
            ref={buttonRef}
            className="material-card-main-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startOrContinue(course);
            }}
            type="primary"
            data-testid="course-button"
          >
            {buttonLabels?.continue || loc.word("Continue")}&nbsp;
            <CaretRightOutlined />
          </Button>
        ) : (
          courseWillBeAvailableBtn(course.studentAccessDate)
        )
      ) : course.studentHasAccess ? (
        course.hasRequirements ? (
          courseRequirementsBtn(course.requirements)
        ) : (
          <Button
            ref={buttonRef}
            className="material-card-main-green-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startOrContinue(course);
            }}
            data-testid="course-button"
          >
            {buttonLabels?.start || loc.word("Start")}
          </Button>
        )
      ) : (
        courseWillBeAvailableBtn(course.studentAccessDate)
      )}
    </div>
  );
};
